import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, cancel, select, takeLatest } from 'redux-saga/effects';

import {
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload,
} from '@ps20-frontend/shop-integration-redux';
import { Fachbaustein, Feature, GegeDatenAntrag, GegenstandAntrag } from '@ps20/bff-models';
import {
  App,
  convertToBffLaufzeit,
  getFachschluessel,
  handleLinieFromVorvertrag,
  mapToBffVorvertragsNummer,
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import dayjs from 'dayjs';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  getSelectedProduktlinie,
  mapGesamtbeitragIfJaehrlichAndBeitragIsWrong,
} from '@ps20-frontend/preis-lib';
import { DATEFORMAT_SHORT_ISO } from '@ps20-frontend/utils';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState } from '../appState';

export function* watchVorschlagdatenChanged(): SagaIterator {
  yield takeLatest(GlobalActions.VORSCHLAGDATEN_REQUESTED, safeSaga(vorschlagdatenSaga, App.GEGE));
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>): SagaIterator {
  try {
    const { angebotsIds } = action.payload;
    const { angebotsId }: IAppState = yield select();

    if (
      !angebotsIds.some(angebot => angebot.appId === App.GEGE && angebot.angebotsId === angebotsId)
    ) {
      return yield cancel();
    }

    const state: IAppState = yield select();
    const globalPut = GlobalPut.getInstance();
    const ausgewaehlteProduktlinie = getSelectedProduktlinie(state.produkt);
    const gegenStaendeAntrag: GegenstandAntrag[] = state.gegenstandsschutz.items.map(gegenstand => {
      return {
        kategorieId: gegenstand.id,
        kaufdatum: dayjs(gegenstand.purchaseDate).format(DATEFORMAT_SHORT_ISO),
        kaufpreis: gegenstand.bruttoPrice,
      };
    });
    const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
    const vorschlagdaten: GegeDatenAntrag = {
      spartenType: state.produkt.spartenType,
      angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
      angebotsId: state.angebotsId,
      jahresbeitrag: state.jahresbeitrag,
      savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
      gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.GEGE,
      ),
      savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
      antragsId: state.antragsnummer,
      sicherungsglaeubiger: null,
      hspLfnr: state.produkt.hspLfnr,
      produkt: {
        beginn: null,
        ablauf: null,
        selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
        produktline: ausgewaehlteProduktlinie.produktName,
        kurztext: ausgewaehlteProduktlinie.kurztext,
        langtext: ausgewaehlteProduktlinie.langtext,
        fachschluessel: setFachschluessel(state),
      },
      rbdMax: ausgewaehlteProduktlinie.rbdMax,
      rbdAktuell: state.taasInfo.rbdNachlass,
      treuebonus: state.taasInfo.tbNachlass,
      savedRbdNachlass: state.taasInfo.savedRbdNachlass,
      savedTbNachlass: state.taasInfo.savedTbNachlass,
      preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
      vorversicherung: [],
      antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
      laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
      rabattstufe: state.rabattstufe,
      geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
      vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
      vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
      zielmarktAbweichung: false,
      gegenstaende: gegenStaendeAntrag,
      kooperation: state.vertragsoptionen.selectedKooperation,
    };

    handleLinieFromVorvertrag(vorschlagdaten, ausgewaehlteProduktlinie, state.vertragsoptionen);

    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten),
    );
  } catch (e) {
    PS20Logger.error(App.GEGE, e as Error, 'VorschlagdatenSaga.vorschlagdatenSaga()');
  }
}

function setFachschluessel(state: IAppState): Array<Fachbaustein> {
  return getFachschluessel(state.produkt);
}
